import React, { useMemo } from "react";

import ArrowRightIcon from "@vahak/core/dist/icons/arrow-right.svg";
import { formatDate } from "@vahak/core-ui/dist/methods";

import useCompactLoadPostingTranslation from "./hooks/useCompactLoadPostingTranslation";
import { getPlaceCityLabel } from "../load-quote";
import { buildCompactLoadSpecParts } from "./buildCompactLoadSpecParts";
import { CompactLoadPostingState } from "./types";
import Styles from "./LoadSummaryChip.module.scss";

interface LoadSummaryChipProps {
    state: CompactLoadPostingState;
    showLoadingTime?: boolean;
    /** hero = header (legacy); body = quiet trip chip; bodyStacked = login stacked summary */
    variant?: "default" | "hero" | "body" | "bodyStacked";
    /** When false, only lorry type is shown for body (no capacity/size/body). */
    includeVehicleDetails?: boolean;
}

const LoadSummaryChip = ({ state, showLoadingTime, variant = "hero", includeVehicleDetails }: LoadSummaryChipProps) => {
    const { t } = useCompactLoadPostingTranslation();
    const isBodyChip = variant === "body";
    const resolvedIncludeDetails =
        includeVehicleDetails ?? (isBodyChip ? false : variant === "bodyStacked" ? true : true);
    const specParts = useMemo(
        () =>
            buildCompactLoadSpecParts(state, {
                includeVehicleDetails: resolvedIncludeDetails,
                // Load Details body chip matches mockup: route + vehicle type only (e.g. LCV).
                includeCapacity: !isBodyChip,
                t
            }),
        [isBodyChip, resolvedIncludeDetails, state, t]
    );

    const sourceLabel = getPlaceCityLabel(state.source);
    const destLabel = getPlaceCityLabel(state.destination);
    const loadingTimeLabel =
        showLoadingTime && state.loadingTimestamp
            ? formatDate(new Date(state.loadingTimestamp * 1000), "DD MMM YYYY, h:mm a")
            : "";

    if (variant === "body" || variant === "bodyStacked") {
        const route = (
            <span className={Styles.bodyRoute}>
                {sourceLabel}
                <span className={Styles.bodyArrow} aria-hidden>
                    {" "}
                    →{" "}
                </span>
                {destLabel}
            </span>
        );
        const specs =
            specParts.length > 0 ? (
                <span className={Styles.bodySpecs}>
                    {specParts.map((part, index) => (
                        <React.Fragment key={`${part}-${index}`}>
                            {index > 0 ? <span aria-hidden> • </span> : null}
                            {part}
                        </React.Fragment>
                    ))}
                </span>
            ) : null;

        if (variant === "bodyStacked") {
            return (
                <div className={Styles.bodyStacked}>
                    <div className={Styles.top}>
                        {route}
                        {specs}
                    </div>
                    {loadingTimeLabel ? (
                        <div className={Styles.timeRow}>
                            <span aria-hidden>🕒</span>
                            <span>{loadingTimeLabel}</span>
                        </div>
                    ) : null}
                </div>
            );
        }

        return (
            <div className={Styles.bodyChip}>
                {route}
                {specs}
            </div>
        );
    }

    return (
        <>
            <div className={`${Styles.chip} ${variant === "hero" ? Styles.chipHero : ""}`}>
                <span className={Styles.city}>{sourceLabel}</span>
                <ArrowRightIcon className={Styles.arrow} width={12} height={12} aria-hidden />
                <span className={Styles.city}>{destLabel}</span>
                {specParts.map((part, index) => (
                    <React.Fragment key={part}>
                        {index === 0 ? (
                            <span className={Styles.separator}>|</span>
                        ) : (
                            <span className={Styles.dot} aria-hidden />
                        )}
                        <span className={Styles.meta}>{part}</span>
                    </React.Fragment>
                ))}
            </div>
            {showLoadingTime && state.loadingTimestamp ? (
                <p className={Styles.loadingTime}>
                    {t("load-form:loadDetails.loadingTimeSummary", {
                        time: formatDate(new Date(state.loadingTimestamp * 1000), "DD MMM YYYY, h:mm a")
                    })}
                </p>
            ) : null}
        </>
    );
};

export default LoadSummaryChip;
